import type { SelectableBasket, Tag, TagValue, UserInstrumentClassificationDto } from "@eu.mdotm.api/sphere";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import Separator from "$root/components/Separator";
import { SideDrawer } from "$root/components/common/SideDrawer";
import { qualifier } from "$root/utils";
import { useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import { DebouncedSearchInput, Icon, type Option, Row, Transition } from "@mdotm/shadcn/legacy/components";
import { Button, IconButton, Select } from "@mdotm/shadcn/mdotm/components";
import { generateUniqueDOMId, useRefProxyWithState } from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach } from "@mdotm/utils/react-extra";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { stableEmptyArray } from "@mdotm/shadcn/legacy/utils";
import { Map, OrderedMap, Set } from "immutable";
import { type ReactNode, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

export const extendedTabWidth = 364;
export const smallTabWidth = 24;

function alwaysMap<K, T>(map?: Map<K, T>): Map<K, T> {
	return map ?? Map();
}

export type TagFilter = Omit<Tag, "standardClassificationId">;

export type InstrumentCustomisationSettings = {
	search: string;
	selectedfilters?: Map<string, TagFilter>;
	selectedBaskets?: Array<SelectableBasket>;
};

export type InstrumentCustomisationTableSidePanelProps = {
	expand: boolean;
	onExpandChange(): void;
	selectableBasketProvider(): Promise<SelectableBasket[]>;
	selectableUserClassificationsProvider(): Promise<UserInstrumentClassificationDto[]>;
	onChangeInstrumentCustomisationSettings(newData: InstrumentCustomisationSettings): void;
	initialInstrumentCustomisationSettings: InstrumentCustomisationSettings;
	noManLand?: ReactNode;
	basketSelection?: {
		disabled?: boolean;
		customTriggerMultiSelection?(selectionSize: number): string;
		hideDelete?: boolean;
	};
};

const emptyInstrumentCustomisationSettings = { search: "", selectedBaskets: undefined, selectedfilters: undefined };

function InstrumentCustomisationTableSidePanel({
	expand,
	onExpandChange,

	selectableBasketProvider,
	selectableUserClassificationsProvider,
	onChangeInstrumentCustomisationSettings,
	initialInstrumentCustomisationSettings,
	noManLand,
	basketSelection,
}: InstrumentCustomisationTableSidePanelProps): React.JSX.Element {
	const { t } = useTranslation();

	const latestInstrumentCustomisationSettings = useRef<InstrumentCustomisationSettings>(
		initialInstrumentCustomisationSettings,
	);

	const [instrumentCustomisationSettings, setInstrumentCustomisationSettings] =
		useRefProxyWithState<InstrumentCustomisationSettings>(
			initialInstrumentCustomisationSettings,
			latestInstrumentCustomisationSettings,
		);

	const querySelectableBaskets = useQueryNoRefetch(["querySelectableBaskets"], {
		queryFn: selectableBasketProvider,
	});

	const querySelectableUserClassifications = useQueryNoRefetch(["querySelectableUserClassifications"], {
		queryFn: selectableUserClassificationsProvider,
	});

	return (
		<SideDrawer
			toggleIcon="Filter-no"
			toggleOffset={64}
			expand={expand}
			onExpandChange={onExpandChange}
			widths={[smallTabWidth, extendedTabWidth]}
			title="Instruments panel"
			footer={
				<div className="flex justify-start">
					<Button
						palette="tertiary"
						size="small"
						onClick={() => {
							setInstrumentCustomisationSettings(emptyInstrumentCustomisationSettings);
							onChangeInstrumentCustomisationSettings(emptyInstrumentCustomisationSettings);
						}}
						data-qualifier={qualifier.instrumentsFiltersPanel.clearAll}
					>
						{t("BUTTON.CLEAR_ALL")}
					</Button>
				</div>
			}
		>
			<div className="relative z-0">
				<Separator text="Filters" align="start" className="mb-2" />
				<DebouncedSearchInput
					externalQuery={instrumentCustomisationSettings.search}
					onChange={(search) => {
						setInstrumentCustomisationSettings((latestSettings) => ({ ...latestSettings, search }));
						onChangeInstrumentCustomisationSettings(latestInstrumentCustomisationSettings.current);
					}}
					placeholder="Search instrument.."
					className="mb-2"
					data-qualifier={qualifier.instrumentsFiltersPanel.search}
					timeout={500}
				/>
				{noManLand}
				<ReactQueryWrapperBase query={querySelectableBaskets}>
					{(selectableBaskets) => {
						const basketOptions = selectableBaskets.map((x) => ({
							label: x.basketName!,
							value: x,
							group: t(`BASKET_ENTITY.${x.basketType && x.labelledAsBenchmark ? "BENCHMARK" : x.basketType!}`),
						}));
						const value = (instrumentCustomisationSettings.selectedBaskets ?? stableEmptyArray).flatMap((selected) => {
							const candidate = basketOptions.find(
								(opt) =>
									opt.value.basketType === selected.basketType &&
									opt.value.basketIdentifier === selected.basketIdentifier,
							);
							if (!candidate) {
								return [];
							}
							return [candidate.value];
						});
						if (value.length === 0 && instrumentCustomisationSettings.selectedBaskets?.length) {
							setInstrumentCustomisationSettings((latestSettings) => ({ ...latestSettings, selectedBaskets: value }));
							onChangeInstrumentCustomisationSettings(latestInstrumentCustomisationSettings.current);
						}
						return (
							<Row alignItems="center" className="mb-2 min-h-[40px]" childrenGrow={1}>
								<Select
									multi
									enableSearch
									options={basketOptions ?? []}
									value={value}
									onChange={(selectedBaskets) => {
										setInstrumentCustomisationSettings((latestSettings) => ({ ...latestSettings, selectedBaskets }));
										onChangeInstrumentCustomisationSettings(latestInstrumentCustomisationSettings.current);
									}}
									placeholder="Basket"
									multiSelectLabel={
										basketSelection?.customTriggerMultiSelection
											? basketSelection.customTriggerMultiSelection
											: (n) => (n === 1 ? "1 basket" : `${n} baskets`)
									}
									disabled={basketSelection?.disabled}
									data-qualifier={qualifier.instrumentsFiltersPanel.basket.options}
									triggerDataAttrs={{
										"data-qualifier": qualifier.instrumentsFiltersPanel.basket.select,
									}}
								/>
								{(instrumentCustomisationSettings.selectedBaskets?.length ?? 0) > 0 && !basketSelection?.hideDelete && (
									<IconButton
										icon="Delete"
										onClick={() => {
											setInstrumentCustomisationSettings((latestSettings) => ({
												...latestSettings,
												selectedBaskets: undefined,
											}));
											onChangeInstrumentCustomisationSettings(latestInstrumentCustomisationSettings.current);
										}}
										size={18}
										color={themeCSSVars.palette_P400}
										className="grow-0"
										data-qualifier={qualifier.instrumentsFiltersPanel.basket.delete}
									/>
								)}
							</Row>
						);
					}}
				</ReactQueryWrapperBase>
				<ReactQueryWrapperBase query={querySelectableUserClassifications}>
					{(selectableUserClassifications) => {
						return (
							<InstrumentCustomisationTableSidePanelFilters
								instrumentCustomisationSettings={instrumentCustomisationSettings}
								selectableUserClassifications={selectableUserClassifications}
								onAddFilter={() =>
									setInstrumentCustomisationSettings((latestSettings) => ({
										...latestSettings,
										selectedfilters: OrderedMap(
											alwaysMap(latestSettings.selectedfilters).set(generateUniqueDOMId(), {}),
										),
									}))
								}
								onChangeTagFilters={(id, payload) =>
									setInstrumentCustomisationSettings((latestSettings) => ({
										...latestSettings,
										selectedfilters: OrderedMap(alwaysMap(latestSettings?.selectedfilters).set(id, payload)),
									}))
								}
								onRemoveTagFilter={(id) =>
									setInstrumentCustomisationSettings((latestSettings) => ({
										...latestSettings,
										selectedfilters: OrderedMap(alwaysMap(latestSettings?.selectedfilters).remove(id)),
									}))
								}
								onCommitTagFilters={() =>
									onChangeInstrumentCustomisationSettings(latestInstrumentCustomisationSettings.current)
								}
							/>
						);
					}}
				</ReactQueryWrapperBase>
			</div>
		</SideDrawer>
	);
}

function optionsByClassificationUuid(
	classificationUuid: string,
	selectableUserClassifications: Array<UserInstrumentClassificationDto>,
): Option<TagValue>[] {
	return (
		selectableUserClassifications
			.find((x) => x.classificationUuid === classificationUuid)
			?.options?.map((x) => ({ value: x, label: x.option! })) ?? []
	);
}

type InstrumentCustomisationTableSidePanelFiltersProps = {
	onAddFilter(): void;

	onChangeTagFilters(id: string, tag: TagFilter): void;
	onCommitTagFilters(): void;
	onRemoveTagFilter(id: string): void;

	instrumentCustomisationSettings: InstrumentCustomisationSettings;
	selectableUserClassifications: Array<UserInstrumentClassificationDto>;
};

function InstrumentCustomisationTableSidePanelFilters({
	onChangeTagFilters,
	onCommitTagFilters,
	onRemoveTagFilter,

	onAddFilter,
	instrumentCustomisationSettings,
	selectableUserClassifications,
}: InstrumentCustomisationTableSidePanelFiltersProps) {
	const selectedFiltersClassificationUuidValue = useMemo(() => {
		const selectedFilters = instrumentCustomisationSettings.selectedfilters?.valueSeq().toArray();
		return Set(selectedFilters?.flatMap((x) => (x.classificationUuid ? [x.classificationUuid] : [])));
	}, [instrumentCustomisationSettings.selectedfilters]);

	const availableColumns = useMemo(
		() =>
			selectableUserClassifications.flatMap((userClassification): Option<string>[] => {
				if (
					userClassification.fieldType !== "TAG" ||
					!userClassification.classificationUuid ||
					!userClassification.name ||
					!(userClassification.options && userClassification.options.length > 0)
				) {
					return [];
				}

				return [
					{
						label: userClassification.name,
						value: userClassification.classificationUuid,
						disabled: selectedFiltersClassificationUuidValue?.has(userClassification.classificationUuid),
					},
				];
			}),
		[selectableUserClassifications, selectedFiltersClassificationUuidValue],
	);

	return (
		<>
			<div className="mb-2">
				<ForEach collection={instrumentCustomisationSettings.selectedfilters?.toArray() ?? []}>
					{([id, opt], index) => (
						<Transition
							in
							initialIn={false}
							className="transition-[opacity,transform,scale] origin-top"
							enterFromClassName="opacity-0 scale-95"
							enterToClassName="opacity-100 scale-100"
						>
							<Row alignItems="center">
								<div className="flex gap-2 flex-1 max-w-[calc(100%-38px)]">
									<Select
										enableSearch
										options={availableColumns}
										value={opt.classificationUuid}
										onChange={(newClassificationUuid) => {
											if (newClassificationUuid) {
												onChangeTagFilters(id, {
													classificationUuid: newClassificationUuid,
													values: [],
												});
											}
											onCommitTagFilters();
										}}
										className="w-[139px]"
										data-qualifier={qualifier.instrumentsFiltersPanel.filters.selectByLevel("columnSelection", index)}
									/>
									{opt.classificationUuid && (
										<Select
											multi
											enableSearch
											options={optionsByClassificationUuid(opt.classificationUuid, selectableUserClassifications)}
											value={opt.values ?? []}
											onChange={(selectedOptions) => {
												if (selectedOptions) {
													onChangeTagFilters(id, { ...opt, values: selectedOptions });
													onCommitTagFilters();
												}
											}}
											className="w-[139px]"
											data-qualifier={qualifier.instrumentsFiltersPanel.filters.selectByLevel("optionSelection", index)}
										/>
									)}
								</div>
								<IconButton
									icon="Delete"
									onClick={() => {
										onRemoveTagFilter(id);
										onCommitTagFilters();
									}}
									size={18}
									color={themeCSSVars.palette_P400}
									className="ml-auto "
								/>
							</Row>
						</Transition>
					)}
				</ForEach>
			</div>
			<Button
				palette="secondary"
				onClick={() => onAddFilter()}
				size="small"
				data-qualifier={qualifier.instrumentsFiltersPanel.filters.add}
			>
				<Icon icon="Outline1" />
			</Button>
		</>
	);
}

export default InstrumentCustomisationTableSidePanel;
